import dayjs, {Dayjs} from 'dayjs';

import {CalendarEntryDto, CalendarTaskDto, CalendarViewDto} from 'apiClient/Api';

/** Farbrolle aus der Theme-Palette. */
export type CalendarColor = 'primary' | 'success' | 'warning' | 'info' | 'secondary';

/**
 * Ein Element im Raster — Plantafel-Eintrag oder Wiedervorlage.
 *
 * Beides wird auf dieselbe Form gebracht, damit Wochen-, Monats- und Listenansicht nur noch eine
 * Sorte kennen. Die Herkunft bleibt über <see cref="kind"/> und die mitgeführten Rohdaten erhalten;
 * der Detaildialog zeigt sie unverändert.
 */
export interface CalendarItem {
    key: string;
    kind: 'entry' | 'task';
    entry?: CalendarEntryDto;
    task?: CalendarTaskDto;
    /** Tag im Format YYYY-MM-DD. */
    date: string;
    /**
     * Ganztägig — oder ohne Uhrzeit. Wiedervorlagen tragen meist keine Zeit, deshalb landen sie
     * im Ganztagsband statt irgendwo im Raster.
     */
    allDay: boolean;
    /** Minuten seit Mitternacht. */
    startMin: number;
    endMin: number;
    title: string;
    subtitle?: string;
    color: CalendarColor;
}

/** Farbe der Kategorie. Aufträge und Abwesenheiten verwaltet Labelwin, nicht diese Oberfläche. */
export const kindColor = (kind?: string | null): CalendarColor => {
    switch (kind) {
        case 'Order':
            return 'primary';
        case 'Appointment':
            return 'success';
        case 'Absence':
            return 'warning';
        case 'Project':
            return 'info';
        default:
            return 'secondary';
    }
};

const minutesOf = (value?: string | null): number => {
    if (!value) return 0;
    const d = dayjs(value);
    return d.hour() * 60 + d.minute();
};

/** Bringt Einträge und Wiedervorlagen auf die gemeinsame Form. */
export const toItems = (view?: CalendarViewDto): CalendarItem[] => {
    const items: CalendarItem[] = [];

    for (const entry of view?.entries ?? []) {
        const date = entry.date
            ? dayjs(entry.date).format('YYYY-MM-DD')
            : entry.start
                ? dayjs(entry.start).format('YYYY-MM-DD')
                : '';
        if (!date) continue;

        const startMin = minutesOf(entry.start);
        const endMin = minutesOf(entry.end);

        items.push({
            key: `e${entry.id}`,
            kind: 'entry',
            entry,
            date,
            // Ohne verwertbare Zeitspanne gehört der Eintrag ins Ganztagsband — sonst bekäme er im
            // Raster die Höhe null und wäre unsichtbar.
            allDay: (entry.isAllDay ?? false) || endMin <= startMin,
            startMin,
            endMin,
            title: entry.subject?.trim() || '—',
            subtitle: [entry.orderNumber, entry.project].filter(Boolean).join(' · ') || undefined,
            color: kindColor(entry.kind),
        });
    }

    for (const task of view?.tasks ?? []) {
        const date = task.date ? dayjs(task.date).format('YYYY-MM-DD') : '';
        if (!date) continue;

        const startMin = minutesOf(task.start);
        const endMin = minutesOf(task.end);

        items.push({
            key: `t${task.id}`,
            kind: 'task',
            task,
            date,
            allDay: !task.start || endMin <= startMin,
            startMin,
            endMin,
            title: task.subject?.trim() || '—',
            subtitle: [task.activity, task.status].filter(Boolean).join(' · ') || undefined,
            color: 'secondary',
        });
    }

    return items;
};

/** Gruppiert nach Tag. */
export const groupByDay = (items: CalendarItem[]): Map<string, CalendarItem[]> => {
    const map = new Map<string, CalendarItem[]>();
    for (const item of items) {
        const list = map.get(item.date) ?? [];
        list.push(item);
        map.set(item.date, list);
    }
    // forEach statt for…of: das Übersetzungsziel des Projekts kann Map-Iteratoren nicht abbilden.
    map.forEach((list) => {
        list.sort((a: CalendarItem, b: CalendarItem) => {
            if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
            return a.startMin - b.startMin || a.endMin - b.endMin;
        });
    });
    return map;
};

/** Ein platzierter Termin: Spur und Spurenzahl bestimmen Breite und Versatz. */
export interface PlacedItem {
    item: CalendarItem;
    lane: number;
    lanes: number;
}

/**
 * Verteilt sich überlappende Termine eines Tages nebeneinander.
 *
 * Bewusst einfach: eine freie Spur suchen, sonst eine neue aufmachen, und die Gesamtzahl der Spuren
 * des Tages als Breite nehmen. Ein exakteres Verfahren würde je Überschneidungsgruppe rechnen und
 * damit einzelne Termine breiter zeichnen — den Aufwand ist es bei einer Plantafel nicht wert, auf
 * der zwei gleichzeitige Termine schon die Ausnahme sind.
 */
export const layoutDay = (items: CalendarItem[]): PlacedItem[] => {
    const timed = items.filter((item) => !item.allDay);
    const laneEnds: number[] = [];
    const placed: {item: CalendarItem; lane: number}[] = [];

    for (const item of timed) {
        let lane = laneEnds.findIndex((end) => end <= item.startMin);
        if (lane === -1) {
            lane = laneEnds.length;
            laneEnds.push(item.endMin);
        } else {
            laneEnds[lane] = item.endMin;
        }
        placed.push({item, lane});
    }

    const lanes = Math.max(1, laneEnds.length);
    return placed.map((p) => ({...p, lanes}));
};

/** Vorgabe-Zeitfenster des Rasters, wenn nichts anderes dagegen spricht. */
export const DEFAULT_DAY_START = 7;
export const DEFAULT_DAY_END = 19;

/**
 * Wählt den anzuzeigenden Stundenbereich: die Vorgabe, erweitert um alles, was tatsächlich im
 * Zeitraum liegt. Ein Termin um 5:30 darf nicht unsichtbar sein, nur weil das Raster um 7 beginnt.
 */
export const hourRange = (items: CalendarItem[]): {from: number; to: number} => {
    let from = DEFAULT_DAY_START;
    let to = DEFAULT_DAY_END;

    for (const item of items) {
        if (item.allDay) continue;
        from = Math.min(from, Math.floor(item.startMin / 60));
        to = Math.max(to, Math.ceil(item.endMin / 60));
    }

    return {from: Math.max(0, from), to: Math.min(24, Math.max(to, from + 1))};
};

/** Die sieben Tage der Woche, in der `anchor` liegt — Montag zuerst. */
export const weekDays = (anchor: Dayjs): Dayjs[] => {
    const monday = anchor.subtract((anchor.day() + 6) % 7, 'day').startOf('day');
    return Array.from({length: 7}, (_, i) => monday.add(i, 'day'));
};

/**
 * Das Monatsraster: volle Wochen von Montag bis Sonntag, damit die Spalten über alle Zeilen
 * denselben Wochentag tragen. Die Tage der Nachbarmonate werden mitgeliefert und blass gezeichnet.
 */
export const monthGridDays = (anchor: Dayjs): Dayjs[] => {
    const first = anchor.startOf('month');
    const start = first.subtract((first.day() + 6) % 7, 'day').startOf('day');
    const last = anchor.endOf('month').startOf('day');
    const end = last.add((7 - ((last.day() + 6) % 7) - 1), 'day');
    const days: Dayjs[] = [];
    for (let d = start; !d.isAfter(end, 'day'); d = d.add(1, 'day')) days.push(d);
    return days;
};

/** Zeitspanne eines Elements als kurzer Text. */
export const timeLabel = (item: CalendarItem, allDayText: string): string => {
    if (item.allDay) return allDayText;
    const pad = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    return `${pad(item.startMin)}–${pad(item.endMin)}`;
};
